/* Management Accounts Editor — 7-step pipeline UI.
   Step components are co-located here (small & cohesive); shared bits live in
   /components/mgmt/shared.jsx and the transaction grid + comparison view live
   in their own files. */
import React, { useEffect, useMemo, useState, useCallback } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import {
  ArrowLeft, CheckCircle, Sparkle, ListChecks, ArrowsLeftRight, Coins,
  Scales, Wallet, Download, UploadSimple,
} from "@phosphor-icons/react";
import { C, kes, Header, Panel, GateBar, UploadCard, NumField, Row } from "@/components/mgmt/shared";
import { TransactionTable } from "@/components/mgmt/TransactionTable";
import { CompareMonths } from "@/components/mgmt/CompareMonths";
import David from "@/components/David";
import AdvisorSessionsInline from "@/components/AdvisorSessionsInline";
import ContinueOnDesktopNotice from "@/components/ContinueOnDesktopNotice";
import LowCreditNudge from "@/components/LowCreditNudge";
import { extractErrorMessage } from "@/lib/apiError";

const STEP_ICONS = {
  collect: <UploadSimple size={14} />,
  clean: <ListChecks size={14} />,
  reconcile: <ArrowsLeftRight size={14} />,
  classify: <Sparkle size={14} />,
  pnl: <Coins size={14} />,
  bs: <Scales size={14} />,
  cf: <Wallet size={14} />,
};

const ClinqMark = () => (
  <span className="relative inline-flex items-baseline font-extrabold tracking-tight text-2xl"
    style={{ fontFamily: "Manrope, sans-serif", letterSpacing: "-0.03em", color: C.teal }}>
    Cl<span className="relative inline-block">ı<span aria-hidden className="absolute left-1/2 -translate-x-1/2 rounded-full clinq-dot-glow"
      style={{ backgroundColor: C.orange, height: "0.5rem", width: "0.5rem", bottom: "1.05em" }}></span></span>nq
  </span>
);

export default function MgmtEditor() {
  const { accountId } = useParams();
  const navigate = useNavigate();
  const [meta, setMeta] = useState(null);
  const [acct, setAcct] = useState(null);
  const [stmts, setStmts] = useState(null);
  const [active, setActive] = useState("collect");
  const [busy, setBusy] = useState(false);

  const refresh = useCallback(async () => {
    const { data } = await api.get(`/mgmt/accounts/${accountId}`);
    setAcct(data);
    try {
      const s = await api.get(`/mgmt/accounts/${accountId}/statements`);
      setStmts(s.data);
    } catch { /* statements not ready */ }
  }, [accountId]);

  useEffect(() => {
    api.get("/mgmt/meta").then(({ data }) => setMeta(data));
    refresh().catch(() => toast.error("Could not load account"));
  }, [refresh]);

  if (!acct || !meta) {
    return <div className="min-h-screen grid place-items-center text-white/50" style={{ backgroundColor: C.bgDark }} data-testid="mgmt-loading">Loading…</div>;
  }

  const coaByCode = Object.fromEntries(meta.chart_of_accounts.map((a) => [a.code, a]));

  return (
    <div className="min-h-screen text-white" style={{ backgroundColor: C.bgDark, fontFamily: "Manrope, sans-serif" }} data-testid="mgmt-editor">
      <header style={{ backgroundColor: "#000" }} className="border-b border-white/5 sticky top-0 z-30">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 py-3 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button onClick={() => navigate("/dashboard")} className="text-white/55 hover:text-white" data-testid="mgmt-back-dashboard">
              <ArrowLeft size={16} />
            </button>
            <Link to="/"><ClinqMark /></Link>
            <div className="hidden sm:flex flex-col leading-none border-l pl-3 border-white/15">
              <span className="text-[9px] uppercase tracking-[0.22em] font-semibold" style={{ color: C.orange }}>Management</span>
              <span className="text-[9px] uppercase tracking-[0.22em] font-semibold mt-0.5 text-white/55">Accounts</span>
            </div>
            <div className="ml-2 text-sm text-white/75">
              <span className="font-bold">{acct.company_name}</span>
              <span className="text-white/40"> · {acct.target_month} · {acct.industry}</span>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={() => exportXlsx(accountId, acct.company_name)} data-testid="mgmt-export-btn"
              className="text-xs font-semibold inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md hover:bg-white/10"
              style={{ border: "1px solid rgba(255,255,255,0.15)" }}>
              <Download size={12} /> Export Excel
            </button>
          </div>
        </div>
        {/* Step nav + Compare tab */}
        <div className="max-w-7xl mx-auto px-6 sm:px-8 pb-3 flex flex-wrap gap-2">
          {meta.steps.map((s) => {
            const done = acct.gates?.[s.id];
            const on = active === s.id;
            return (
              <button key={s.id} onClick={() => setActive(s.id)} data-testid={`mgmt-step-${s.id}`}
                className="text-[11px] font-semibold uppercase tracking-[0.15em] inline-flex items-center gap-2 px-3 py-1.5 rounded-full border transition-colors"
                style={{
                  backgroundColor: on ? C.teal : "rgba(0,0,0,0.35)",
                  borderColor: on ? C.teal : (done ? "rgba(10,139,133,0.4)" : "rgba(255,255,255,0.1)"),
                  color: on ? "#fff" : (done ? C.tealSoft : "rgba(255,255,255,0.6)"),
                }}>
                {done && !on ? <CheckCircle size={12} weight="fill" /> : STEP_ICONS[s.id]}
                <span>{s.num}. {s.name}</span>
              </button>
            );
          })}
          <span className="ml-auto" />
          <button onClick={() => setActive("compare")} data-testid="mgmt-step-compare"
            className="text-[11px] font-semibold uppercase tracking-[0.15em] inline-flex items-center gap-2 px-3 py-1.5 rounded-full border transition-colors"
            style={{
              backgroundColor: active === "compare" ? C.orange : "rgba(0,0,0,0.35)",
              borderColor: active === "compare" ? C.orange : "rgba(255,255,255,0.1)",
              color: active === "compare" ? "#000" : "rgba(255,255,255,0.6)",
            }}>
            Compare months
          </button>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 sm:px-8 py-8">
        <ContinueOnDesktopNotice surface="the reconciliation flow" />
        <div className="mb-6 max-w-2xl">
          <AdvisorSessionsInline compact />
        </div>
        {active === "collect" && <Step1Collect acct={acct} busy={busy} setBusy={setBusy} refresh={refresh} />}
        {active === "clean" && <Step2Clean acct={acct} coa={coaByCode} refresh={refresh} />}
        {active === "reconcile" && <Step3Reconcile acct={acct} stmts={stmts} busy={busy} setBusy={setBusy} refresh={refresh} />}
        {active === "classify" && <Step4Classify acct={acct} coa={coaByCode} chart={meta.chart_of_accounts} busy={busy} setBusy={setBusy} refresh={refresh} />}
        {active === "pnl" && <Step5PnL stmts={stmts} acct={acct} refresh={refresh} />}
        {active === "bs" && <Step6BS stmts={stmts} acct={acct} refresh={refresh} />}
        {active === "cf" && <Step7CF stmts={stmts} acct={acct} refresh={refresh} />}
        {active === "compare" && <CompareMonths company={acct.company_name} />}
      </main>

      <David />
      <LowCreditNudge />
    </div>
  );
}

async function exportXlsx(accountId, company) {
  try {
    const res = await api.get(`/mgmt/accounts/${accountId}/export`, { responseType: "blob" });
    const url = URL.createObjectURL(new Blob([res.data]));
    const a = document.createElement("a"); a.href = url;
    a.download = `${(company || "business").replace(/\s+/g, "_")}_management_accounts.xlsx`;
    a.click(); URL.revokeObjectURL(url);
    toast.success("Excel downloaded");
  } catch { toast.error("Export failed"); }
}

// ============================================================
// STEP 1 — Collect
// ============================================================
function Step1Collect({ acct, busy, setBusy, refresh }) {
  const opening = acct.opening || {};
  const closing = acct.closing || {};
  const [form, setForm] = useState({
    opening_bank: opening.opening_bank ?? 0,
    opening_mpesa: opening.opening_mpesa ?? 0,
    opening_cash: opening.opening_cash ?? 0,
    closing_bank: closing.closing_bank ?? 0,
    closing_mpesa: closing.closing_mpesa ?? 0,
    closing_cash: closing.closing_cash ?? 0,
  });

  const saveOpening = async () => {
    setBusy(true);
    try {
      await api.put(`/mgmt/accounts/${acct.account_id}/opening-balances`, form);
      toast.success("Balances saved");
      await refresh();
    } catch { toast.error("Could not save"); } finally { setBusy(false); }
  };

  const upload = async (source, file, replace = false) => {
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post(`/mgmt/accounts/${acct.account_id}/upload?source=${source}&replace=${replace ? "true" : "false"}`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success(`${data.added} transactions ${replace ? "loaded (replaced previous)" : "added"} (${source})`);
      await refresh();
    } catch (e) {
      toast.error(extractErrorMessage(e, "Upload failed"));
    } finally { setBusy(false); }
  };

  const confirmGate = async () => {
    try {
      await api.post(`/mgmt/accounts/${acct.account_id}/gate`, { step_id: "collect" });
      toast.success("Step 1 confirmed");
      await refresh();
    } catch (e) { toast.error(extractErrorMessage(e, "Cannot confirm yet")); }
  };

  const countBySrc = (src) => (acct.transactions || []).filter((t) => t.source === src).length;

  return (
    <div className="space-y-8" data-testid="mgmt-step-collect-panel">
      <Header step={1} title="Collect & organise" desc="Upload your Excel ledger, bank statement and M-Pesa statement for the target month, then enter opening and closing balances. Re-uploads can replace the previous file." />

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <UploadCard label="Excel ledger" source="XL" count={countBySrc("XL")} onFile={(f, r) => upload("XL", f, r)} testid="mgmt-upload-xl" busy={busy} />
        <UploadCard label="Bank statement" source="BNK" count={countBySrc("BNK")} onFile={(f, r) => upload("BNK", f, r)} testid="mgmt-upload-bnk" busy={busy} />
        <UploadCard label="M-Pesa statement" source="MPS" count={countBySrc("MPS")} onFile={(f, r) => upload("MPS", f, r)} testid="mgmt-upload-mps" busy={busy} />
      </div>

      <Panel title="Opening & closing balances (KES)">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-4">
          <NumField label="Opening bank" v={form.opening_bank} on={(v) => setForm({ ...form, opening_bank: v })} testid="mgmt-opening-bank" />
          <NumField label="Closing bank" v={form.closing_bank} on={(v) => setForm({ ...form, closing_bank: v })} testid="mgmt-closing-bank" />
          <NumField label="Opening M-Pesa float" v={form.opening_mpesa} on={(v) => setForm({ ...form, opening_mpesa: v })} testid="mgmt-opening-mpesa" />
          <NumField label="Closing M-Pesa float" v={form.closing_mpesa} on={(v) => setForm({ ...form, closing_mpesa: v })} testid="mgmt-closing-mpesa" />
          <NumField label="Opening cash in hand" v={form.opening_cash} on={(v) => setForm({ ...form, opening_cash: v })} testid="mgmt-opening-cash" />
          <NumField label="Closing cash in hand" v={form.closing_cash} on={(v) => setForm({ ...form, closing_cash: v })} testid="mgmt-closing-cash" />
        </div>
        <button onClick={saveOpening} disabled={busy} data-testid="mgmt-save-balances"
          className="mt-5 px-4 py-2 rounded-md font-semibold text-xs"
          style={{ backgroundColor: C.teal, color: "#fff" }}>Save balances</button>
      </Panel>

      <GateBar gateId="collect" done={acct.gates?.collect} onConfirm={confirmGate} requirements={[
        `Transactions uploaded: ${countBySrc("XL") + countBySrc("BNK") + countBySrc("MPS")}`,
        `Opening balances set: ${opening.opening_bank !== undefined ? "yes" : "no"}`,
      ]} />
    </div>
  );
}

// ============================================================
// STEP 2 — Clean
// ============================================================
function Step2Clean({ acct, coa, refresh }) {
  const txns = acct.transactions || [];
  return (
    <div className="space-y-6" data-testid="mgmt-step-clean-panel">
      <Header step={2} title="Clean & standardise" desc={`Review every transaction — fix dates, descriptions or amounts that look wrong. Click trash to drop a duplicate row. ${txns.length} transactions parsed.`} />
      <TransactionTable txns={txns} acct={acct} coa={coa} refresh={refresh} mode="clean" />
      <GateBar gateId="clean" done={acct.gates?.clean}
        onConfirm={async () => { try { await api.post(`/mgmt/accounts/${acct.account_id}/gate`, { step_id: "clean" }); toast.success("Step 2 confirmed"); await refresh(); } catch (e) { toast.error(extractErrorMessage(e, "Cannot confirm")); } }}
        requirements={["All transactions reviewed, duplicates dropped, dates within target month."]} />
    </div>
  );
}

// ============================================================
// STEP 3 — Reconcile
// ============================================================
function Step3Reconcile({ acct, stmts, busy, setBusy, refresh }) {
  const [result, setResult] = useState(null);
  const run = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/mgmt/accounts/${acct.account_id}/reconcile`);
      setResult(data);
      await refresh();
      toast.success(`Reconciliation done — ${data.internal_transfer_legs} transfer legs flagged`);
    } catch (e) { toast.error(extractErrorMessage(e, "Reconciliation failed")); } finally { setBusy(false); }
  };
  const recon = stmts?.recon || {};
  return (
    <div className="space-y-6" data-testid="mgmt-step-reconcile-panel">
      <Header step={3} title="Reconcile the three sources" desc="Detect internal bank ↔ M-Pesa transfers (so they aren't double-counted) and surface any unmatched flows that still need a payee/payer." />

      <button onClick={run} disabled={busy} data-testid="mgmt-run-reconcile"
        className="px-4 py-2.5 rounded-md font-semibold text-xs inline-flex items-center gap-2"
        style={{ backgroundColor: C.teal, color: "#fff" }}>
        <ArrowsLeftRight size={12} /> Run reconciliation
      </button>

      {result && (
        <Panel title="Reconciliation results">
          <Row label="Internal transfer legs flagged" v={result.internal_transfer_legs} />
          <Row label="Excel rows still unclassified" v={result.unmatched_xl} />
          <Row label="Bank rows still unclassified" v={result.unmatched_bnk} />
          <Row label="M-Pesa rows still unclassified" v={result.unmatched_mps} />
        </Panel>
      )}

      <Panel title="Cash reconciliation summary">
        <Row label="Opening cash (bank + M-Pesa + hand)" v={kes(recon.opening_total)} />
        <Row label="Inflows (excluding transfers)" v={kes(recon.inflows)} />
        <Row label="Outflows (excluding transfers)" v={kes(recon.outflows)} />
        <Row label="Internal transfers (excluded)" v={kes(recon.internal_transfers)} />
        <Row label="Closing cash (per statements)" v={kes(recon.closing_total)} />
        <Row label="Variance (should be 0)" v={kes(recon.variance)} bold={Math.abs(recon.variance || 0) > 1} warn={Math.abs(recon.variance || 0) > 1} />
      </Panel>

      <GateBar gateId="reconcile" done={acct.gates?.reconcile}
        onConfirm={async () => { try { await api.post(`/mgmt/accounts/${acct.account_id}/gate`, { step_id: "reconcile" }); toast.success("Step 3 confirmed"); await refresh(); } catch (e) { toast.error(extractErrorMessage(e, "Cannot confirm")); } }}
        requirements={["Internal transfers flagged.", "No unidentified large flows."]} />
    </div>
  );
}

// ============================================================
// STEP 4 — Classify
// ============================================================
function Step4Classify({ acct, coa, chart, busy, setBusy, refresh }) {
  const txns = acct.transactions || [];
  const unclassified = txns.filter((t) => !t.dropped && !t.internal_transfer && (!t.account_code || !t.confirmed));

  const runAI = async () => {
    if (!unclassified.length) { toast.info("Nothing left to classify"); return; }
    setBusy(true);
    try {
      const { data } = await api.post(`/mgmt/accounts/${acct.account_id}/classify-suggest`, {});
      toast.success(`${data.suggested || 0} suggestions returned by AI — confirm each below`);
      await refresh();
    } catch (e) { toast.error(extractErrorMessage(e, "AI failed")); } finally { setBusy(false); }
  };

  return (
    <div className="space-y-6" data-testid="mgmt-step-classify-panel">
      <Header step={4} title="Classify every transaction" desc={`Assign each line to one account code. The AI proposes a code — you confirm or override. ${unclassified.length} unclassified left.`} />

      <div className="flex items-center gap-3">
        <button onClick={runAI} disabled={busy || !unclassified.length} data-testid="mgmt-ai-classify"
          className="px-4 py-2.5 rounded-md font-semibold text-xs inline-flex items-center gap-2 disabled:opacity-50"
          style={{ backgroundColor: C.teal, color: "#fff" }}>
          <Sparkle size={12} weight="fill" /> Suggest classifications (AI)
        </button>
        <span className="text-xs text-white/45">Human-in-the-loop · you confirm every code</span>
      </div>

      <TransactionTable txns={txns} acct={acct} coa={coa} chart={chart} refresh={refresh} mode="classify" />

      <GateBar gateId="classify" done={acct.gates?.classify}
        onConfirm={async () => { try { await api.post(`/mgmt/accounts/${acct.account_id}/gate`, { step_id: "classify" }); toast.success("Step 4 confirmed"); await refresh(); } catch (e) { toast.error(extractErrorMessage(e, "Cannot confirm")); } }}
        requirements={["Every non-dropped, non-transfer transaction has a CONFIRMED account code.", "Nothing left in 9999 (suspense)."]} />
    </div>
  );
}

// ============================================================
// STEP 5 — P&L
// ============================================================
function Step5PnL({ stmts, acct, refresh }) {
  if (!stmts) return <Header step={5} title="Profit & Loss" desc="Run reconciliation first." />;
  const p = stmts.pnl;
  const lines = [
    ["Revenue", p.revenue_total, "head"],
    ["Less: Cost of sales", -p.cogs_total, "sub"],
    ["Gross profit", p.gross_profit, "total"],
    ["Less: Operating expenses", -p.opex_total, "sub"],
    ["Operating profit", p.operating_profit, "total"],
    ["Less: Interest", -p.interest, "sub"],
    ["Less: Tax", -p.tax, "sub"],
    ["Net profit", p.net_profit, "total"],
  ];
  return (
    <div className="space-y-6" data-testid="mgmt-step-pnl-panel">
      <Header step={5} title="Profit & Loss Statement" desc="Derived automatically from the classified ledger. Re-run classification if something looks wrong." />
      <Panel>
        <table className="w-full text-sm">
          <tbody>
            {lines.map(([label, val, kind]) => (
              <tr key={label} className={kind === "total" ? "border-t border-white/10" : ""}>
                <td className={`py-2 ${kind === "head" || kind === "total" ? "font-bold" : "text-white/75 pl-4"}`}
                  style={kind === "total" ? { color: C.teal } : {}}>{label}</td>
                <td className={`py-2 text-right font-mono-num ${kind === "head" || kind === "total" ? "font-bold" : ""}`}
                  style={kind === "total" ? { color: C.teal } : {}}>{kes(val)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </Panel>

      <Panel title="By account">
        <BreakdownTable group="Revenue" rows={p.revenue_by_account} />
        <BreakdownTable group="Cost of sales" rows={p.cogs_by_account} />
        <BreakdownTable group="Operating expenses" rows={p.opex_by_account} />
        <BreakdownTable group="Non-operating" rows={p.nonop_by_account} />
      </Panel>

      <GateBar gateId="pnl" done={acct.gates?.pnl}
        onConfirm={async () => { try { await api.post(`/mgmt/accounts/${acct.account_id}/gate`, { step_id: "pnl" }); toast.success("Step 5 confirmed"); await refresh(); } catch (e) { toast.error(extractErrorMessage(e, "Cannot confirm")); } }}
        requirements={["P&L looks correct — every line traces back to a confirmed account code."]} />
    </div>
  );
}

function BreakdownTable({ group, rows }) {
  const entries = Object.entries(rows || {});
  if (!entries.length) return null;
  return (
    <div className="mb-5">
      <div className="text-[10px] uppercase tracking-[0.22em] font-bold mb-2" style={{ color: C.tealSoft }}>{group}</div>
      <table className="w-full text-xs">
        <tbody>
          {entries.sort(([a], [b]) => a.localeCompare(b)).map(([code, info]) => (
            <tr key={code} className="border-t border-white/5">
              <td className="py-1.5 font-mono-num text-white/45">{code}</td>
              <td className="py-1.5">{info.name}</td>
              <td className="py-1.5 text-right font-mono-num">{kes(info.amount)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ============================================================
// STEP 6 — Balance Sheet
// ============================================================
function Step6BS({ stmts, acct, refresh }) {
  const [bs, setBs] = useState(acct.bs_manual || {});
  const [saving, setSaving] = useState(false);

  if (!stmts) return <Header step={6} title="Balance Sheet" desc="Run reconciliation first." />;
  const b = stmts.bs;
  const setVal = (k, v) => setBs({ ...bs, [k]: Number(v || 0) });

  const save = async () => {
    setSaving(true);
    try { await api.put(`/mgmt/accounts/${acct.account_id}/bs-manual`, bs); toast.success("Saved"); await refresh(); }
    catch { toast.error("Could not save"); } finally { setSaving(false); }
  };

  return (
    <div className="space-y-6" data-testid="mgmt-step-bs-panel">
      <Header step={6} title="Balance Sheet" desc="Cash lines come from your closing balances. Fill in receivables, payables, inventory and loans — the rest is derived." />

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <Panel title="Assets · Liabilities · Equity">
          <table className="w-full text-sm">
            <tbody>
              <BSLine label="Cash at bank" v={b.cash_bank} />
              <BSLine label="Cash on M-Pesa float" v={b.cash_mpesa} />
              <BSLine label="Cash in hand" v={b.cash_hand} />
              <BSLine label="Accounts receivable" v={b.ar} />
              <BSLine label="Inventory" v={b.inventory} />
              <BSLine label="Fixed assets (net)" v={b.fa} />
              <BSLine label="Prepayments & other" v={b.prepaid} />
              <BSLine label="Total assets" v={b.total_assets} bold />
              <tr className="border-t border-white/5"><td colSpan="2"></td></tr>
              <BSLine label="Accounts payable" v={b.ap} />
              <BSLine label="Short-term loans" v={b.st_loans} />
              <BSLine label="Long-term loans" v={b.lt_loans} />
              <BSLine label="VAT / tax payable" v={b.vat} />
              <BSLine label="Accrued expenses" v={b.accruals} />
              <BSLine label="Total liabilities" v={b.total_liabilities} bold />
              <tr className="border-t border-white/5"><td colSpan="2"></td></tr>
              <BSLine label="Owner's capital" v={b.capital} />
              <BSLine label="Owner's drawings" v={-b.drawings} />
              <BSLine label="Retained earnings" v={b.retained} />
              <BSLine label="Total equity" v={b.total_equity} bold />
              <tr className="border-t border-white/10">
                <td className="py-2 font-bold" style={{ color: C.teal }}>Balance check (= 0)</td>
                <td className="py-2 text-right font-mono-num font-bold"
                  style={{ color: Math.abs(b.balance_check) > 1 ? "#F87171" : C.teal }}>{kes(b.balance_check)}</td>
              </tr>
            </tbody>
          </table>
        </Panel>

        <Panel title="Manual inputs (KES)">
          <div className="grid grid-cols-2 gap-3">
            <NumField label="Receivables (AR)" v={bs.ar ?? 0} on={(v) => setVal("ar", v)} testid="mgmt-bs-ar" />
            <NumField label="Payables (AP)" v={bs.ap ?? 0} on={(v) => setVal("ap", v)} testid="mgmt-bs-ap" />
            <NumField label="Inventory" v={bs.inventory ?? 0} on={(v) => setVal("inventory", v)} testid="mgmt-bs-inventory" />
            <NumField label="Fixed assets" v={bs.fa ?? 0} on={(v) => setVal("fa", v)} testid="mgmt-bs-fa" />
            <NumField label="Prepayments" v={bs.prepaid ?? 0} on={(v) => setVal("prepaid", v)} testid="mgmt-bs-prepaid" />
            <NumField label="ST loans" v={bs.st_loans ?? 0} on={(v) => setVal("st_loans", v)} testid="mgmt-bs-st-loans" />
            <NumField label="LT loans" v={bs.lt_loans ?? 0} on={(v) => setVal("lt_loans", v)} testid="mgmt-bs-lt-loans" />
            <NumField label="VAT payable" v={bs.vat ?? 0} on={(v) => setVal("vat", v)} testid="mgmt-bs-vat" />
            <NumField label="Accruals" v={bs.accruals ?? 0} on={(v) => setVal("accruals", v)} testid="mgmt-bs-accruals" />
            <NumField label="Capital injected" v={bs.capital ?? 0} on={(v) => setVal("capital", v)} testid="mgmt-bs-capital" />
            <NumField label="Owner drawings" v={bs.drawings ?? 0} on={(v) => setVal("drawings", v)} testid="mgmt-bs-drawings" />
            <NumField label="Opening retained" v={bs.retained_opening ?? 0} on={(v) => setVal("retained_opening", v)} testid="mgmt-bs-retained" />
          </div>
          <button onClick={save} disabled={saving} data-testid="mgmt-save-bs"
            className="mt-4 px-4 py-2 rounded-md font-semibold text-xs"
            style={{ backgroundColor: C.teal, color: "#fff" }}>Save & recompute</button>
        </Panel>
      </div>

      <GateBar gateId="bs" done={acct.gates?.bs}
        onConfirm={async () => { try { await api.post(`/mgmt/accounts/${acct.account_id}/gate`, { step_id: "bs" }); toast.success("Step 6 confirmed"); await refresh(); } catch (e) { toast.error(extractErrorMessage(e, "Cannot confirm")); } }}
        requirements={["Balance check is at (or very close to) zero."]} />
    </div>
  );
}

function BSLine({ label, v, bold }) {
  return (
    <tr className={bold ? "border-t border-white/10" : ""}>
      <td className={`py-1.5 ${bold ? "font-bold" : "text-white/75"}`} style={bold ? { color: C.teal } : {}}>{label}</td>
      <td className={`py-1.5 text-right font-mono-num ${bold ? "font-bold" : ""}`} style={bold ? { color: C.teal } : {}}>{kes(v)}</td>
    </tr>
  );
}

// ============================================================
// STEP 7 — Cashflow
// ============================================================
function Step7CF({ stmts, acct, refresh }) {
  if (!stmts) return <Header step={7} title="Cashflow" desc="Build statements first." />;
  const c = stmts.cf;
  const goldenOk = Math.abs(c.closing_cash_diff) < 1;
  const lines = [
    ["OPERATING ACTIVITIES", null, "head"],
    ["Net profit", c.net_profit, "sub"],
    ["+ Depreciation (non-cash)", c.depreciation, "sub"],
    ["± Working capital change", c.working_capital_change, "sub"],
    ["Cash from operations", c.operating, "total"],
    ["INVESTING ACTIVITIES", null, "head"],
    ["Asset purchases / disposals", c.investing, "sub"],
    ["FINANCING ACTIVITIES", null, "head"],
    ["Loan capital movements", c.loan_capital, "sub"],
    ["Owner drawings", -c.drawings, "sub"],
    ["Capital injected", c.capital_injected, "sub"],
    ["Cash from financing", c.financing, "total"],
    ["Net change in cash", c.net_change, "total"],
    ["Opening cash", c.opening_cash, "sub"],
    ["Closing cash (per statement)", c.closing_cash_statement, "total"],
    ["Closing cash (per ledger)", c.closing_cash_ledger, "sub"],
  ];

  return (
    <div className="space-y-6" data-testid="mgmt-step-cf-panel">
      <Header step={7} title="Cashflow Statement (indirect)" desc="The Golden Rule: closing cash per cashflow statement must equal bank + M-Pesa + cash in hand." />

      <Panel>
        <table className="w-full text-sm">
          <tbody>
            {lines.map(([label, val, kind], i) => (
              <tr key={`${label}-${i}`} className={kind === "head" ? "border-t border-white/10" : ""}>
                <td className={`py-2 ${kind === "head" || kind === "total" ? "font-bold" : "text-white/75 pl-4"}`}
                  style={kind === "head" || kind === "total" ? { color: C.teal } : {}}>{label}</td>
                <td className={`py-2 text-right font-mono-num ${kind === "head" || kind === "total" ? "font-bold" : ""}`}
                  style={kind === "head" || kind === "total" ? { color: C.teal } : {}}>{val === null ? "" : kes(val)}</td>
              </tr>
            ))}
            <tr className="border-t border-white/10">
              <td className="py-2 font-bold" style={{ color: goldenOk ? C.teal : "#F87171" }}>
                Golden Rule check (target = 0)
              </td>
              <td className="py-2 text-right font-mono-num font-bold"
                style={{ color: goldenOk ? C.teal : "#F87171" }}>{kes(c.closing_cash_diff)}</td>
            </tr>
          </tbody>
        </table>
      </Panel>

      <Panel title="KPIs">
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          {Object.entries(stmts.kpis || {}).map(([k, v]) => (
            <div key={k} className="rounded-md p-4 border" style={{ borderColor: "rgba(123, 211, 206, 0.12)", backgroundColor: "rgba(0,0,0,0.25)" }}>
              <div className="text-[10px] uppercase tracking-[0.18em] font-bold text-white/45">{k.replace(/_/g, " ")}</div>
              <div className="text-xl font-extrabold mt-1" style={{ color: C.teal }}>
                {v === null || v === undefined || Number.isNaN(v) ? "—" :
                  (k.endsWith("margin") ? `${Number(v).toFixed(1)}%` :
                    (k.endsWith("days") || k === "cash_conversion_cycle" ? `${Math.round(v)} d` : Number(v).toFixed(2)))}
              </div>
            </div>
          ))}
        </div>
      </Panel>

      <GateBar gateId="cf" done={acct.gates?.cf}
        onConfirm={async () => { try { await api.post(`/mgmt/accounts/${acct.account_id}/gate`, { step_id: "cf" }); toast.success("Cashflow signed off — pack ready"); await refresh(); } catch (e) { toast.error(extractErrorMessage(e, "Cannot confirm")); } }}
        requirements={[`Golden Rule variance: ${kes(c.closing_cash_diff)}`]} />
    </div>
  );
}
